import React, { useMemo } from "react";
import { Button, Card, Col, DatePicker, Form, Input, Row, Select } from "antd";
import SubscriptionDrawerShell from "./SubscriptionDrawerShell";
import {
  CURRENCY_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_TYPE_OPTIONS,
  SUBSCRIBER_TYPE_OPTIONS,
  SUBSCRIPTION_STATUS_OPTIONS,
  isFreePlanRecord,
  getSubscriptionFormValues,
} from "./subscriptionConstants";

export default function SubscriptionDrawer({
  open,
  editingSubscription,
  form,
  saving,
  planLookup,
  planLookupOptions,
  onClose,
  onSave,
}) {
  const selectedPlanId = Form.useWatch("subscriptionPlanId", form);
  const selectedPlan = useMemo(
    () => planLookup.find((plan) => plan.id === selectedPlanId) || null,
    [planLookup, selectedPlanId],
  );
  const isFreeSubscription = selectedPlan
    ? isFreePlanRecord(selectedPlan)
    : isFreePlanRecord(editingSubscription);

  return (
    <SubscriptionDrawerShell
      open={open}
      title={editingSubscription ? "Edit subscription" : "Create subscription"}
      width={880}
      onClose={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={onSave} loading={saving}>
          {editingSubscription ? "Save changes" : "Create"}
        </Button>,
      ]}
    >
      {!planLookup.length ? (
        <Card size="small" style={{ marginBottom: 16, borderRadius: 12 }}>
          <span style={{ color: "#6b7280" }}>
            Create at least one subscription plan first so you can attach it to subscriptions.
          </span>
        </Card>
      ) : null}

      <Form layout="vertical" form={form} initialValues={getSubscriptionFormValues()}>
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item
              name="subscriberType"
              label="Subscriber type"
              rules={[{ required: true, message: "Select a subscriber type" }]}
            >
              <Select options={SUBSCRIBER_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item
              name="subscriberId"
              label="Subscriber ID"
              rules={[{ required: true, message: "Enter the subscriber UUID" }]}
            >
              <Input placeholder="Subscriber UUID" />
            </Form.Item>
          </Col>
          <Col xs={24} md={24}>
            <Form.Item
              name="subscriptionPlanId"
              label="Subscription plan"
              rules={[{ required: true, message: "Select a subscription plan" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="Choose a plan"
                options={planLookupOptions}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="subscriptionPaymentType" label="Payment type">
              <Select
                options={PAYMENT_TYPE_OPTIONS}
                disabled={isFreeSubscription}
                placeholder={isFreeSubscription ? "Free subscription" : undefined}
              />
            </Form.Item>
          </Col>
          {!isFreeSubscription ? (
            <>
              <Col xs={24} md={8}>
                <Form.Item name="paymentStatus" label="Payment status">
                  <Select options={PAYMENT_STATUS_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="currency" label="Currency">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select currency"
                    options={CURRENCY_OPTIONS}
                  />
                </Form.Item>
              </Col>
            </>
          ) : null}
          <Col xs={24} md={8}>
            <Form.Item name="status" label="Status">
              <Select options={SUBSCRIPTION_STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="startDate"
              label="Start date"
              rules={[{ required: true, message: "Select a start date" }]}
            >
              <DatePicker
                showTime={{ format: "hh:mm A" }}
                format="MMMM D, YYYY hh:mm A"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="endDate" label="End date">
              <DatePicker
                showTime={{ format: "hh:mm A" }}
                format="MMMM D, YYYY hh:mm A"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </SubscriptionDrawerShell>
  );
}
